/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import Media from 'react-media';

import { Grid, PageGridContainer, breakpoints } from '@coursera/cds-core';

import BrowseFAQsModuleRenderer from 'bundles/browse-common/renderers/BrowseFAQsModuleRenderer';
import AnyCollectionRenderer from 'bundles/browse-common/renderers/ComplexCollectionRenderer/AnyCollectionRenderer';
import DomainListRenderer from 'bundles/browse-common/renderers/DomainListRenderer';
import ExploreRolesRenderer from 'bundles/browse-common/renderers/ExploreRolesRenderer';
import LoggedOutProgressiveOnboardingRenderer from 'bundles/browse-common/renderers/LoggedOutProgressiveOnboardingRenderer';
import RelatedSkillsRenderer from 'bundles/browse-common/renderers/RelatedSkillsRenderer';
import TopPartnersComponentRenderer from 'bundles/browse-common/renderers/TopPartnersComponentRenderer';
import UpsellCardModuleRenderer from 'bundles/browse-common/renderers/UpsellCardModuleRenderer';
import { getShouldShowCourseraPlusMerchandisingPlacements } from 'bundles/coursera-plus-common/utils/subscriptionsForwardDiscoveryExperiment';
import CoachOnboardingBannerModuleRenderer from 'bundles/page-config-common/module-renderers/CoachOnboardingBannerModuleRenderer';
import PageConfigComponentMetadataProvider from 'bundles/page-config-common/providers/PageConfigComponentMetadataProvider';
import type { PageConfig_ComponentConfigFragment as PageConfigComponent } from 'bundles/page-config-common/queries/page-config/__generated__/PageConfigComponentsQuery';
import { useUserAgent } from 'bundles/page/contexts/UserAgentApiContext';
import SearchFrame from 'bundles/search-common/components/SearchFrame';
import CoreSkillsForDomainRenderer from 'bundles/search-common/components/page-config/CoreSkillsForDomainRenderer';
import ExperimentalComponentModuleRenderer from 'bundles/search-common/components/page-config/ExperimentalComponentModuleRenderer';
import FreeQueryValuePropositionRenderer from 'bundles/search-common/components/page-config/FreeQueryValuePropositionRenderer';
import MediumIntentSearchValuePropositionRenderer from 'bundles/search-common/components/page-config/MediumIntentSearchValuePropositionRenderer';
import MostPopularFreeBySkillsCollectionModuleRenderer from 'bundles/search-common/components/page-config/MostPopularFreeBySkillsCollectionModuleRenderer';
import MostPopularFreeCollectionModuleRenderer from 'bundles/search-common/components/page-config/MostPopularFreeCollectionModuleRenderer';
import MostPopularFreeInRoleCollectionModuleRenderer from 'bundles/search-common/components/page-config/MostPopularFreeInRoleCollectionModuleRenderer';
import PartnersMediumIntentSearchValuePropositionRenderer from 'bundles/search-common/components/page-config/PartnersMediumIntentSearchValuePropositionRenderer';
import PathwayFreeTrialCollectionModuleRenderer from 'bundles/search-common/components/page-config/PathwayFreeTrialCollectionModuleRenderer';
import SkillsForRoleRenderer from 'bundles/search-common/components/page-config/SkillsForRoleRenderer';
import TopCourseraPartnersRenderer from 'bundles/search-common/components/page-config/TopCourseraPartnersRenderer';
import SearchBar from 'bundles/search-common/components/searchbar/SearchBar';
import { MAX_SCREEN_WIDTH_FOR_MOBILE_SEARCH_BAR } from 'bundles/search-common/constants';
import { FILTER_TYPE, PAGINATION_TYPE } from 'bundles/search-common/constants/filter';
import { MediumIntentProvider } from 'bundles/search-common/providers/MediumIntentProvider';
import useIsEqp from 'bundles/search-common/utils/useIsEqp';

const styles = {
  container: css`
    padding-bottom: var(--cds-spacing-600);
  `,
  moduleSpacing: css`
    margin-top: var(--cds-spacing-300);
    margin-bottom: var(--cds-spacing-300);
    ${breakpoints.down('sm')} {
      margin-top: var(--cds-spacing-200);
      margin-bottom: var(--cds-spacing-200);
    }
  `,
  valuePropSpacing: css`
    margin-top: var(--cds-spacing-400);
    margin-bottom: var(--cds-spacing-200);
  `,
  careerAcademySpacing: css`
    > section {
      margin-top: var(--cds-spacing-200);
      margin-bottom: var(--cds-spacing-200);
    }
  `,
  mobileSearchBarFix: css`
    .rc-SearchBar {
      margin: 0 !important;
      width: 100% !important;
    }
  `,
};

export type Props = {
  pageConfigComponents?: Array<PageConfigComponent | undefined | null> | null;
};

type RenderComponentProps = {
  component: PageConfigComponent;
  pageConfigComponents: Array<PageConfigComponent | undefined | null>;
};

const RenderComponent = ({ component, pageConfigComponents }: RenderComponentProps) => {
  const isSEOEntityPage = useIsEqp();

  switch (component?.__typename) {
    case 'PageConfig_BrowseValuePropositionComponent':
      return (
        <PageGridContainer css={[styles.moduleSpacing, styles.valuePropSpacing]}>
          <Grid item xs={12}>
            <FreeQueryValuePropositionRenderer key={component.id} />
          </Grid>
        </PageGridContainer>
      );

    case 'PageConfig_MostPopularFreeCollection':
      return <MostPopularFreeCollectionModuleRenderer key={component.id} pageConfigComponent={component} />;
    case 'PageConfig_ExploreRolesComponent':
      return (
        <div css={styles.careerAcademySpacing}>
          <ExploreRolesRenderer pageConfigComponent={component} />
        </div>
      );

    case 'PageConfig_BrowseRelatedSkillsComponent':
      return (
        <PageGridContainer css={styles.moduleSpacing}>
          <Grid item xs={12}>
            <RelatedSkillsRenderer pageConfig={component} />
          </Grid>
        </PageGridContainer>
      );

    case 'PageConfig_PathwayFreeTrialCollection':
      return <PathwayFreeTrialCollectionModuleRenderer key={component.id} pageConfigComponent={component} />;

    case 'PageConfig_BrowseLoggedOutProgressiveOnboardingComponent':
      return (
        <PageGridContainer css={styles.moduleSpacing}>
          <Grid item xs={12}>
            <LoggedOutProgressiveOnboardingRenderer />
          </Grid>
        </PageGridContainer>
      );

    case 'PageConfig_SearchFrameComponent':
      return (
        <SearchFrame
          config={{
            // TODO: apply dynamic configs when available https://coursera.atlassian.net/browse/SEARCH-1437
            paginationType: isSEOEntityPage ? PAGINATION_TYPE.PAGINATED : PAGINATION_TYPE.INFINITE,
            enableFilters: true,
            filterType: isSEOEntityPage ? FILTER_TYPE.EXPANDABLE : FILTER_TYPE.INLINE,
            showSortBy: !isSEOEntityPage,
            showRelatedQueries: true,
            showCplusUpsell: true,
            isSEOEntityPage,
            enableFeaturedSnippet: isSEOEntityPage,
            enableSchemaMarkup: isSEOEntityPage,
            enableSEOMetatags: isSEOEntityPage,
            enableScrollToResultsOnPageChange: true,
            facets: component.facets && component.facets.length > 0 ? component.facets : undefined,
          }}
        />
      );

    case 'PageConfig_BrowseDomainListComponent':
      return (
        <PageGridContainer css={styles.moduleSpacing}>
          <Grid item xs={12}>
            <DomainListRenderer pageConfig={component} />
          </Grid>
        </PageGridContainer>
      );

    case 'PageConfig_TopPartnersComponent':
      return (
        <PageGridContainer css={styles.moduleSpacing}>
          <Grid item xs={12}>
            <TopPartnersComponentRenderer />
          </Grid>
        </PageGridContainer>
      );

    case 'PageConfig_FAQComponent':
      return <BrowseFAQsModuleRenderer />;

    case 'PageConfig_UpsellComponent':
      return <UpsellCardModuleRenderer />;

    case 'PageConfig_MostPopularFreeByRoleCollection':
      return <MostPopularFreeInRoleCollectionModuleRenderer key={component.id} pageConfigComponent={component} />;

    case 'PageConfig_MostPopularFreeBySkillCollection':
      return <MostPopularFreeBySkillsCollectionModuleRenderer key={component.id} pageConfigComponent={component} />;

    case 'PageConfig_CoachOnboardingBanner':
      return <CoachOnboardingBannerModuleRenderer key={component.id} />;

    case 'PageConfig_TopCourseraPartnersComponent':
      return (
        <PageGridContainer css={styles.moduleSpacing}>
          <Grid item xs={12}>
            <TopCourseraPartnersRenderer key={component.id} />
          </Grid>
        </PageGridContainer>
      );

    case 'PageConfig_MediumIntentSearchValuePropositionComponent':
      return (
        <PageGridContainer css={[styles.moduleSpacing, styles.valuePropSpacing]}>
          <Grid item xs={12}>
            <MediumIntentSearchValuePropositionRenderer />
          </Grid>
        </PageGridContainer>
      );

    case 'PageConfig_MediumIntentSearchValuePropositionForPartnerComponent':
      return (
        <PageGridContainer css={[styles.moduleSpacing, styles.valuePropSpacing]}>
          <Grid item xs={12}>
            <PartnersMediumIntentSearchValuePropositionRenderer />
          </Grid>
        </PageGridContainer>
      );

    case 'PageConfig_CoreSkillsComponent':
      return (
        <PageGridContainer css={styles.moduleSpacing}>
          <Grid item xs={12}>
            <CoreSkillsForDomainRenderer key={component.id} />
          </Grid>
        </PageGridContainer>
      );

    case 'PageConfig_CoreSkillsForRoleComponent':
      return (
        <PageGridContainer css={styles.moduleSpacing}>
          <Grid item xs={12}>
            <SkillsForRoleRenderer key={component.id} />
          </Grid>
        </PageGridContainer>
      );

    case 'PageConfig_CollectionComponent': {
      return (
        <AnyCollectionRenderer
          key={component.id}
          pageComponentList={pageConfigComponents}
          pageConfigComponent={component}
        />
      );
    }

    case 'PageConfig_ExperimentalComponent':
      return <ExperimentalComponentModuleRenderer pageConfigComponent={component} />;

    default:
      return null;
  }
};

/**
 * @description Components config renderer for search pages
 * Handles rendering of components for CONSUMER_SEARCH and BROWSE_EQP_FREE page configurations
 */
const SearchComponentsConfigRenderer = ({ pageConfigComponents }: Props) => {
  // Make an impression
  getShouldShowCourseraPlusMerchandisingPlacements();
  const userAgent = useUserAgent();

  if (!pageConfigComponents || !pageConfigComponents.length) {
    return null;
  }

  return (
    <div css={styles.container}>
      <PageGridContainer maxWidth="lg">
        <Grid item xs={12}>
          <Media
            query={{ maxWidth: MAX_SCREEN_WIDTH_FOR_MOBILE_SEARCH_BAR }}
            defaultMatches={userAgent?.isMobileBrowser || userAgent?.isMobile}
          >
            {(matches) =>
              matches && (
                <div className="mobile-search-page-bar" css={styles.mobileSearchBarFix}>
                  <SearchBar />
                </div>
              )
            }
          </Media>
        </Grid>
      </PageGridContainer>
      <MediumIntentProvider>
        {pageConfigComponents.filter(Boolean).map((pageConfigComponent, index) => (
          <PageConfigComponentMetadataProvider
            key={pageConfigComponent?.id}
            id={pageConfigComponent?.id}
            index={index}
            name={pageConfigComponent.__typename}
          >
            <RenderComponent component={pageConfigComponent} pageConfigComponents={pageConfigComponents} />
          </PageConfigComponentMetadataProvider>
        ))}
      </MediumIntentProvider>
    </div>
  );
};

export default SearchComponentsConfigRenderer;
